import React, {useState,useEffect} from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FormControl, MenuItem, Paper, Select, SelectChangeEvent } from '@mui/material';

import AlertInfo from '../../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import WebsiteTotalSummary from './WebsiteTotalSummary';
import { WebsiteTotalCountSummary } from '../dataObjects/WebsiteTotalCountSummary';
import WebsiteDailyChart from './WebsiteDailyLineChart';
import { WebsiteDailyCountSummary } from '../dataObjects/WebsiteDailyCountSummary';
import WebsiteRealTimeSummary from './WebsiteRealTimeSummary';
import { CityUserDetail, WebsiteRealTimeCountSummary } from '../dataObjects/WebsiteRealTimeCountSummary';
import { WebsiteTopVisitedPageSummary } from '../dataObjects/WebsiteTopVisitedPageSummary';
import WebsiteTopVisitedPagesSummaryTable from './WebsiteTopVisitedPagesSummaryTable';
import WebsiteUserByCityChart from './WebsiteUserByCityChart';


const OcpfUsTraffic = () => {

    const [currentPeriod, setCurrentPeriod] = useState<string>("7daysAgo")
    const [countSummary, setCountSummary] = useState<WebsiteTotalCountSummary>();
    const [dailySummary, setDailySummary] = useState<WebsiteDailyCountSummary[]>();
    const [realTimeSummary, setRealTimeSummary] = useState<WebsiteRealTimeCountSummary>();
    const [topVisitedPages, setTopVisitedPages] = useState<WebsiteTopVisitedPageSummary[]>();
    const [topCities, setTopCities] = useState<CityUserDetail[]>();

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    //payload
    useEffect(() => {
        //maybe later get the data every 5 minutes
        getRealTimeSummary();
    }, [])

    useEffect(() => {
        const getTotalCountSummary = async () => {
            await fetch(`/ItDashboard/GetTotalSummary?startDate=${currentPeriod}`)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        let info = {
                            type: 'error',
                            title: 'Error: ItDashboard - GetTotalSummary',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        setCountSummary(result);
                    }
                }
                )
        }
        const getDailyTrendSummary = async () => {
            await fetch(`/ItDashboard/GetDailyTrendSummary?startDate=${currentPeriod}`)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        let info = {
                            type: 'error',
                            title: 'Error: ItDashboard - GetDailyTrendSummary',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        setDailySummary(result);
                    }
                }
                )
        }
        const getTopVisitedPagesSummary = async () => {
            await fetch(`/ItDashboard/GetTopVisitedPages?startDate=${currentPeriod}`)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        let info = {
                            type: 'error',
                            title: 'Error: ItDashboard - GetTopVisitedPages',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        setTopVisitedPages(result);
                    }
                }
                )
        }
        const getUsersByCity = async () => {
            await fetch(`/ItDashboard/GetUsersByCity?startDate=${currentPeriod}`)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        let info = {
                            type: 'error',
                            title: 'Error: ItDashboard - GetUsersByCity',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        setTopCities(result);
                    }
                }
                )
        }
        getTotalCountSummary();
        getDailyTrendSummary();
        getTopVisitedPagesSummary();
        getUsersByCity();
    }, [currentPeriod]);

    const getRealTimeSummary = async () => {
        await fetch(`/ItDashboard/GetRealtimeData`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: ItDashboard - GetRealtimeData',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setRealTimeSummary(result);
                }
            }
        )
    }

    const switchPeriod = (event: SelectChangeEvent<string>) => {
        let period = event.target.value;
        setCurrentPeriod(period);
    };

    return (
        <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
            <Stack direction="row" spacing={2} marginTop={2} marginBottom={3} alignItems="center" display="flex" justifyContent="flex-start">
                <Typography component="h2" variant="h6" sx={{ mb: 2, mt: 2 }}>
                    OCPF.US Traffic Summary
                </Typography>
                <FormControl>
                    <Select
                        value={currentPeriod}
                        onChange={switchPeriod}
                        size="small"
                    >
                        <MenuItem value="7daysAgo">Last 7 days</MenuItem>
                        <MenuItem value="14daysAgo">Last 14 days</MenuItem>
                        <MenuItem value="30daysAgo">Last 30 days</MenuItem>
                        <MenuItem value="60daysAgo">Last 60 days</MenuItem>
                    </Select>
                </FormControl>
            </Stack>
            <Grid
                container
                spacing={2}
                columns={12}
                sx={{ mb: (theme) => theme.spacing(2) }}
            >
               {/*Total Count*/}
                <Grid item xs={8} component={Paper} >
                    <WebsiteTotalSummary summary={countSummary} ></WebsiteTotalSummary>
                    <WebsiteDailyChart summary={dailySummary} ></WebsiteDailyChart>
                </Grid>
                <Grid item xs={0.2}></Grid>
                {/* Real time */}
                <Grid item xs={3.8} component={Paper} >
                    <WebsiteRealTimeSummary summary={realTimeSummary} ></WebsiteRealTimeSummary>
                </Grid>
            </Grid>
            <Grid container spacing={2} columns={12} marginTop={4} >
                <Grid item xs={8} component={Paper} >
                    <WebsiteTopVisitedPagesSummaryTable pages={topVisitedPages} ></WebsiteTopVisitedPagesSummaryTable>
                </Grid>
                <Grid item xs={0.2}></Grid>
                <Grid item xs={3.8} component={Paper} >
                    <WebsiteUserByCityChart cities={topCities} ></WebsiteUserByCityChart>
                </Grid>
            </Grid>
            {
              alertInfo &&
              <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </Box>
  );
}

export default OcpfUsTraffic;
